import { gql, useMutation, useQuery } from "@apollo/client";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { Dispatch, FC, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { loggedInUser, tzOverride } from "src/apollo/cache";
import { EmptyState } from "src/Components/Segments/DashboardSideBarSegments/UpcomingDials";
import { AppText, Loading, SkeletonBlock, Spinner } from "src/Components/UI";
import { FlexDiv } from "src/Components/UI/FlexDiv";
import { PhoenixAppButton, PhoenixIcon, PhoenixMultiSelect } from "src/Components/UI/Phoenix";
import { LeadCardContext, LeadFilterContext, ModalContext } from "src/context";
import { alert_circle, calendar, filter, phone_call } from "src/images/NewDesign";
import { MixpanelActions } from "src/services/mixpanel";
import { LeadCardParentQueue, OptionItem } from "src/types";
import { QUEUE_SORT_OPTIONS } from "src/utils/misc";
import { theme } from "src/utils/theme";
import { errorToast } from "src/utils/toast";
import {
  ACTIVE_QUEUE_TAKE,
  APP_ENABLEMENT_ROLES,
  APP_ROLES,
  FETCH_CURRENT_LEAD_POLL_INTERVAL,
  PINNED_QUEUE_TAKE,
  QUEUE_LIMIT,
} from "src/utils/variables";
import styled, { keyframes } from "styled-components";
import { CompactLeadCard, CompactLeadCardLead, SearchLeadModule } from "./";

const formatLoadingText = (selectedTabDialQueue: LeadCardParentQueue) => {
  if (selectedTabDialQueue === "custom") {
    return "Finding your custom queue leads";
  }

  if (selectedTabDialQueue === "pinned") {
    return "Finding your pinned leads";
  }

  if (selectedTabDialQueue === "active") {
    return "Finding the best leads";
  }
  return "Finding the best leads";
};
const CUSTOM_TASK_ACK = gql`
  mutation customTaskAck($customTaskAckInput: customTaskAckInput!) {
    customTaskAck(customTaskAckInput: $customTaskAckInput)
  }
`;

const FETCH_UPCOMING = gql`
  query fetchUpcoming($lead_filter: LeadFilter, $override_timezone_contraints: Boolean) {
    fetchNextDial(lead_filter: $lead_filter, override_timezone_contraints: $override_timezone_contraints) {
      id
      lead_id
      type
      is_scheduled_item
      lead {
        id
        favorited
        primary_phone_number
        current_lead_type
        city
        state
        country
        lead_value {
          value
        }
        timezone_by_state
        lead_source
        industry
        sub_industry
        address
        channel
        business_name
        full_name
        first_name
        last_name
        lead_ownership_status
        rep_id
        computed_mrr
        current_close_date
        last_lead_activity {
          id
          type
          created_at
        }
        organization {
          id
          name
          allow_remove_step_action
          allow_snooze_step_action
        }
        sequence_id
        sequence_step_id
        sequence_step {
          actions {
            id
            task
            custom_task_note
            scheduled_event_behavior
          }
        }
        next_lead_intent {
          id
          schedule_item {
            id
            deleted_at
          }
        }
        next_intent_scheduled_or_unscheduled {
          id
          replacement_lead_intent_id
        }
        # TODO: conditionally query visible_custom_fields if LeadCardTopInfoV2 requires custom field data
        visible_custom_fields {
          id
          key
          value
          visible
          boo_value
          num_value
          date_value
          type
          lead_id
          options
          computed_value
          list_value
          allow_reps_to_edit
        }
      }
      schedule_item {
        id
        start_time
        end_time
        google_space_sellfire_id
        google_space_info {
          google_space_sellfire_id
          id
          meeting_url
          meeting_code
        }
        zoom_meeting_sellfire_id
        zoom_meeting_info {
          zoom_meeting_sellfire_id
          meeting_url
          meeting_id
        }
      }
    }
  }
`;

interface UpcomingLeadIntent {
  id: string;
  lead: CompactLeadCardLead;
  lead_id: string;
  type: string;
  user_id: string;
  is_scheduled_item: boolean;
  schedule_item: {
    google_space_sellfire_id?: string;
    google_space_info?: {
      id: string;
      meeting_url?: string;
      meeting_code?: string;
    };
    zoom_meeting_sellfire_id?: string;
    zoom_meeting_info?: {
      id: string;
      meeting_url?: string;
      meeting_id?: string;
    };
  };
}

interface IQueue {
  selectedQueueIndex: number;
  setSelectedQueueIndex: Dispatch<SetStateAction<number>>;
  highlightedLead: any;
  setHighlightedLead: Dispatch<SetStateAction<any>>;
  firstLoad: boolean;
  setFirstLoad: Dispatch<SetStateAction<boolean>>;
}

export const Queue: FC<IQueue> = ({
  selectedQueueIndex,
  setSelectedQueueIndex,
  highlightedLead,
  setHighlightedLead,
  firstLoad,
  setFirstLoad,
}) => {
  const {
    sortOptionDialQueue,
    setSortOptionDialQueue,
    selectedTabDialQueue,
    setSelectedTabDialQueue,
    upcomingDials,
    setUpcomingDials,
    setCustomDials,
    activeQueueSkipRef,
    activeQueueSkip,
    activeQueueHasMore,
    hasMoreCustom,
    hasMorePinned,
    fetchActiveQueue,
    fetchCustomLeadsInQueue,
    errorActiveQueue,
    customDials,
    pinnedDials,
    customQueueSkipRef,
    customQueueSkip,
    loadingQueue,
    setResetActiveQueue,
    setResetCustomQueue,
    pinnedQueueSkip,
    setPinnedSkip,
    pinnedQueueSkipRef,
    resetPinnedQueue,
    fetchPinnedLeads,
    setResetPinnedQueue,
  } = useContext(LeadCardContext);

  const { showFiltersModal, setShowFiltersModal, setShowClearCustomQueueModal } = useContext(ModalContext);

  const { filterNum, leadFilter } = useContext(LeadFilterContext);

  const { showPinnedQueue } = useFlags();

  const titleIsBusinessName = loggedInUser()?.organization?.title_is_business_name;

  const maxCardContentHeight = useRef<number | undefined>(undefined);

  const selectedIntentId = useMemo(() => upcomingDials[selectedQueueIndex]?.id, [selectedQueueIndex, upcomingDials]);

  useEffect(() => {
    const dialMap = {
      active: upcomingDials,
      custom: customDials,
      pinned: pinnedDials,
    };

    const currentDials = dialMap[selectedTabDialQueue as keyof typeof dialMap];

    if (currentDials && (!!currentDials.length || (!currentDials.length && !loadingQueue))) {
      firstLoad && setFirstLoad(false);
    }
  }, [upcomingDials, customDials, pinnedDials, firstLoad, loadingQueue, selectedTabDialQueue]);
  const queueItems = useMemo(
    () =>
      selectedTabDialQueue === "active"
        ? upcomingDials
        : selectedTabDialQueue === "custom"
        ? customDials
        : selectedTabDialQueue === "pinned"
        ? pinnedDials
        : [],
    [selectedTabDialQueue, upcomingDials, customDials, pinnedDials],
  );

  useEffect(() => {
    if (selectedTabDialQueue === "active") {
      setResetActiveQueue(true);
      fetchActiveQueue({
        variables: {
          skip: 0,
          take: ACTIVE_QUEUE_TAKE,
          lead_filter: leadFilter,
          sort_option: sortOptionDialQueue,
        },
      });
    } else if (selectedTabDialQueue === "custom") {
      setResetCustomQueue(true);
      fetchCustomLeadsInQueue({
        variables: {
          skip: 0,
          take: ACTIVE_QUEUE_TAKE,
          lead_filter: leadFilter,
          sort_option: sortOptionDialQueue,
          includeVisibleCustomFields: true,
        },
      });
    } else {
      setResetPinnedQueue(true);
      fetchPinnedLeads({
        variables: {
          skip: 0,
          take: PINNED_QUEUE_TAKE,
          lead_filter: leadFilter,
          sort_option: sortOptionDialQueue,
        },
      });
    }
  }, [leadFilter, sortOptionDialQueue, selectedTabDialQueue]);

  const handleShowMore = useCallback(() => {
    if (selectedTabDialQueue === "active") {
      activeQueueSkipRef.current = activeQueueSkip + ACTIVE_QUEUE_TAKE;
      fetchActiveQueue({
        variables: {
          skip: activeQueueSkip + ACTIVE_QUEUE_TAKE,
          take: ACTIVE_QUEUE_TAKE,
          lead_filter: leadFilter,
          sort_option: sortOptionDialQueue,
        },
      });
    } else if (selectedTabDialQueue === "custom") {
      customQueueSkipRef.current = customQueueSkip + ACTIVE_QUEUE_TAKE;
      fetchCustomLeadsInQueue({
        variables: {
          skip: customQueueSkip + ACTIVE_QUEUE_TAKE,
          take: ACTIVE_QUEUE_TAKE,
          lead_filter: leadFilter,
          sort_option: sortOptionDialQueue,
        },
      });
    } else if (selectedTabDialQueue === "pinned") {
      pinnedQueueSkipRef.current = pinnedQueueSkip + PINNED_QUEUE_TAKE;

      fetchPinnedLeads({
        variables: {
          skip: pinnedQueueSkip + PINNED_QUEUE_TAKE,
          take: PINNED_QUEUE_TAKE,
          lead_filter: leadFilter,
          sort_option: sortOptionDialQueue,
        },
      });
    }
  }, [
    fetchActiveQueue,
    activeQueueSkipRef.current,
    leadFilter,
    sortOptionDialQueue,
    selectedTabDialQueue,
    customQueueSkipRef.current,
    activeQueueSkip,
    customQueueSkip,
    pinnedQueueSkip,
    pinnedQueueSkipRef,
    fetchPinnedLeads,
    pinnedQueueSkipRef.current,
  ]);

  const [customTaskAck] = useMutation(CUSTOM_TASK_ACK, {
    onCompleted({ customTaskAck: leadIntentId }) {
      // remove from active queue
      const filteredActive = upcomingDials.slice().filter((item: any) => item?.id !== leadIntentId);

      setUpcomingDials(filteredActive);

      upcomingDials.length < QUEUE_LIMIT - 2 &&
        fetchActiveQueue({
          variables: {
            skip: activeQueueSkip === 0 ? ACTIVE_QUEUE_TAKE : activeQueueSkip,
            take: 4,
            lead_filter: leadFilter,
            sort_option: sortOptionDialQueue,
          },
        });
    },
    onError({ message }) {
      errorToast(message);
      console.log("Error marking custom task: ", message);
    },
  });

  const { startPolling: fetchUpcomingPoll } = useQuery(FETCH_UPCOMING, {
    fetchPolicy: "network-only",
    variables: {
      lead_filter: leadFilter,
      override_timezone_contraints: tzOverride(),
    },
    skip: APP_ENABLEMENT_ROLES.includes(loggedInUser().role as APP_ROLES),
    notifyOnNetworkStatusChange: true,
    onCompleted({ fetchNextDial }) {
      if (fetchNextDial?.[0]?.is_scheduled_item) {
        setHighlightedLead(fetchNextDial[0]);
      } else {
        setHighlightedLead(null);
      }
    },
    onError({ message, name }) {
      console.log(`Error in ${name}: `, message);
    },
  });
  useEffect(() => fetchUpcomingPoll(FETCH_CURRENT_LEAD_POLL_INTERVAL), []);

  const emptyActiveQueue = useMemo(
    () =>
      !errorActiveQueue && !loadingQueue && !firstLoad && selectedTabDialQueue === "active" && !upcomingDials.length,
    [errorActiveQueue, loadingQueue, firstLoad, selectedTabDialQueue, upcomingDials],
  );

  const InfiniteScrollDataLength = useMemo(() => {
    return selectedTabDialQueue === "active"
      ? upcomingDials.length
      : selectedTabDialQueue === "custom"
      ? customDials.length
      : selectedTabDialQueue === "pinned"
      ? pinnedDials.length
      : 0;
  }, [selectedTabDialQueue, upcomingDials, customDials, pinnedDials]);

  const InfiniteScrollHasMore = useMemo(() => {
    return selectedTabDialQueue === "active"
      ? activeQueueHasMore
      : selectedTabDialQueue === "custom"
      ? hasMoreCustom
      : selectedTabDialQueue === "pinned"
      ? hasMorePinned
      : false;
  }, [selectedTabDialQueue, activeQueueHasMore, hasMoreCustom, hasMorePinned]);

  return (
    <QueueContainer selectedQueue={selectedTabDialQueue}>
      <SearchLeadModule />

      <QueueMenu>
        <FlexDiv style={{ marginTop: "auto" }}>
          <QueueMenuButton
            selected={selectedTabDialQueue === "active"}
            onClick={() => {
              setSelectedTabDialQueue("active");
              setSelectedQueueIndex(0);
              if (!upcomingDials.length) {
                setFirstLoad(true);
                fetchActiveQueue({
                  variables: {
                    skip: 0,
                    take: ACTIVE_QUEUE_TAKE,
                    lead_filter: leadFilter,
                    sort_option: sortOptionDialQueue,
                  },
                });
              }
            }}
          >
            Suggested
          </QueueMenuButton>
          <QueueMenuButton
            selected={selectedTabDialQueue === "custom"}
            onClick={() => {
              setSelectedTabDialQueue("custom");
              setSelectedQueueIndex(0);
              if (!customDials.length) {
                setFirstLoad(true);
                fetchCustomLeadsInQueue({
                  variables: {
                    skip: 0,
                    take: ACTIVE_QUEUE_TAKE,
                    lead_filter: leadFilter,
                    sort_option: sortOptionDialQueue,
                  },
                });
              }
            }}
          >
            Custom
          </QueueMenuButton>
          {showPinnedQueue && (
            <QueueMenuButton
              selected={selectedTabDialQueue === "pinned"}
              onClick={() => {
                setSelectedTabDialQueue("pinned");
                setSelectedQueueIndex(0);
                if (!pinnedDials.length) {
                  setFirstLoad(true);
                  fetchPinnedLeads({
                    variables: {
                      skip: 0,
                      take: PINNED_QUEUE_TAKE,
                      lead_filter: leadFilter,
                      sort_option: sortOptionDialQueue,
                    },
                  });
                }
              }}
            >
              Pinned
            </QueueMenuButton>
          )}
        </FlexDiv>

        <QueueMenuFill />

        <FlexDiv
          align="center"
          gap={16}
          padding="0px 0px 8px 0px"
          style={{ borderBottom: `1px solid ${theme.PILL_DARK_GRAY}` }}
        >
          <PhoenixMultiSelect
            isMulti={false}
            name="queue_sort"
            options={QUEUE_SORT_OPTIONS}
            placeholder="Sort"
            onChange={(e: OptionItem) => {
              setSortOptionDialQueue(String(e.value));
            }}
            value={{
              label: "Sort",
              value: sortOptionDialQueue,
            }}
            isClearable={false}
            marginBottom={false}
            error={false}
            ghostSelect
            ghostSelectAppThemeOverride="Dark"
            width={50}
            optionsContainerWidth={186}
            style={{ overflow: "hidden" }}
            lineHeight="10px"
            pointer
            menuInset="40px 0px 0px -130px"
            menuBorder
            removeMenuPadding
          />
          <QueueFilterButton
            onClick={() => {
              MixpanelActions.track("Queue Event", {
                type: "filter",
                tab: `${selectedTabDialQueue}`,
                view: "Dashboard",
              });
              setShowFiltersModal(!showFiltersModal);
            }}
          >
            {!!filterNum && <NotificationsNumber />}
            <PhoenixIcon
              svg={filter}
              color={theme.icon.neutral.secondary}
              hoverColor={theme.icon.neutral.secondary}
              size={16}
              pointer
            />
          </QueueFilterButton>
        </FlexDiv>
      </QueueMenu>

      {!errorActiveQueue && !!firstLoad && <LoadingState selectedTabDialQueue={selectedTabDialQueue} />}

      {emptyActiveQueue && (
        <div style={{ background: theme.fill.brand.tertiary, borderRadius: "8px", margin: "24px" }}>
          <EmptyState />
        </div>
      )}

      {!!errorActiveQueue && (
        <FlexDiv direction="column" justify="center" align="center" height="100%" gap={16}>
          <PhoenixIcon
            svg={alert_circle}
            size={48}
            color={theme.icon.neutral.default}
            hoverColor={theme.icon.neutral.default}
          />
          <AppText
            fontSize={12}
            fontWeight={500}
            color={theme.text.neutral.inverse}
            lineHeight={18}
            style={{ paddingBottom: "140px" }}
          >
            Something went wrong with loading your queue
          </AppText>
        </FlexDiv>
      )}
      {!errorActiveQueue && (
        <IntentContainer id="upcoming-scroll-div">
          {!firstLoad && !emptyActiveQueue && (
            <FlexDiv
              justify="space-between"
              align="center"
              padding={selectedQueueIndex >= 0 ? "0px 0px 8px 0px" : "0px"}
            >
              <FlexDiv align="center" gap={8}>
                <PhoenixIcon
                  svg={highlightedLead?.is_scheduled_item ? calendar : phone_call}
                  size={16}
                  color={theme.icon.warning.default}
                  hoverColor={theme.icon.warning.default}
                />
                <AppText fontSize={12} fontWeight={500} lineHeight={18} color={theme.text.neutral.inverse}>
                  {highlightedLead?.is_scheduled_item ? "Upcoming Event" : "Next Action"}
                </AppText>
              </FlexDiv>
              {selectedTabDialQueue === "custom" && customDials?.length > 0 && (
                <PhoenixAppButton
                  variant="brand"
                  buttonType="ghost-small"
                  textColor="#0282F9"
                  uppercase
                  onClick={() => {
                    // show confirmation modal
                    setShowClearCustomQueueModal(true);
                  }}
                >
                  Clear Queue
                </PhoenixAppButton>
              )}
            </FlexDiv>
          )}
          <InfiniteScroll
            dataLength={InfiniteScrollDataLength}
            next={handleShowMore}
            hasMore={InfiniteScrollHasMore}
            loader={<Loading />}
            scrollableTarget="upcoming-scroll-div"
          >
            {!firstLoad && !!highlightedLead && (
              <UpcomingEventContainer direction="column" expanded={selectedQueueIndex === -1}>
                <CompactLeadCard
                  index={-1}
                  intentData={highlightedLead}
                  titleIsBusinessName={titleIsBusinessName}
                  key={highlightedLead.id}
                  maxCardContentHeight={maxCardContentHeight}
                  selectedQueueIndex={selectedQueueIndex}
                  setSelectedQueueIndex={setSelectedQueueIndex}
                  customTaskAck={customTaskAck}
                  expectedActionOverride="manualCall"
                  parentQueue={undefined}
                />
              </UpcomingEventContainer>
            )}

            {queueItems?.map((leadIntent: UpcomingLeadIntent, i: number) => (
              <CompactLeadCard
                index={i}
                intentData={leadIntent}
                titleIsBusinessName={titleIsBusinessName}
                key={leadIntent.id}
                selectedQueueIndex={selectedQueueIndex}
                setSelectedQueueIndex={setSelectedQueueIndex}
                selectedIntentId={selectedIntentId}
                maxCardContentHeight={maxCardContentHeight}
                disableAnimations={queueItems.length >= 40}
                customTaskAck={customTaskAck}
                parentQueue={selectedTabDialQueue}
              />
            ))}
          </InfiniteScroll>
        </IntentContainer>
      )}
    </QueueContainer>
  );
};

const QueueContainer = styled.div<{ selectedQueue: LeadCardParentQueue }>`
  position: relative;
  z-index: 10;

  display: flex;
  flex-direction: column;
  gap: 16px;

  width: 472px;
  min-width: 472px;
  max-height: 100vh;
  min-height: 100vh;

  padding-top: 16px;

  overflow-y: auto;

  background: linear-gradient(180deg, #1a2a6b 0%, #3252cf 100%);
  background-size: 100% 300%;

  animation: queueStartup 0.5s ease-in-out forwards;

  @keyframes queueStartup {
    0% {
      width: 300px;
      min-width: 300px;
      opacity: 0.8;
      background-size: 100% 300%;
    }
    100% {
      width: 472px;
      min-width: 472px;
      opacity: 1;
      background-size: 100% 100%;
    }
  }

  ::before {
    content: "";
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;

    width: 100%;
    height: 100vh;

    background: linear-gradient(180deg, #003452 0%, #2fb4ff 100%);
    opacity: ${({ selectedQueue }) => (selectedQueue === "custom" ? 1 : 0)};
    transition: opacity 0.4s ease-in-out;
  }
  ::after {
    content: "";
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;

    width: 100%;
    height: 100vh;

    background: linear-gradient(180deg, #08333d 0%, #40d1e1 100%);
    opacity: ${({ selectedQueue }) => (selectedQueue === "pinned" ? 1 : 0)};
    transition: opacity 0.4s ease-in-out;
  }
`;

const QueueMenu = styled.div`
  display: flex;
  justify-content: space-between;

  padding: 0px 24px;

  animation: ${theme.heavyPopup} 0.5s ease-in-out;
`;

const QueueMenuButton = styled.button<{ selected: boolean }>`
  width: 102px;
  height: 32px;

  padding: 8px 16px;

  color: ${({ selected }) => (selected ? theme.text.neutral.inverse : theme.text.neutral.secondary)};
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 1px;
  text-transform: uppercase;

  border: none;
  border-bottom: ${({ selected }) => (selected ? "2px" : "1px")} solid
    ${({ selected }) => (selected ? theme.border.brand.primary : theme.PILL_DARK_GRAY)};

  background-color: transparent;

  :hover {
    color: ${theme.text.neutral.inverse};
    cursor: pointer;
  }

  transition: border 0.2s ease-in-out, color 0.1s ease-in-out;
`;

const QueueMenuFill = styled.div`
  width: 100%;
  border-bottom: 1px solid ${theme.PILL_DARK_GRAY};
`;

const NotificationsNumber = styled.div`
  z-index: 2;
  position: absolute;
  top: 4px;
  right: 2px;

  width: 10px;
  height: 10px;

  cursor: pointer;
  border-radius: 50%;
  background: ${theme.ATTENTION700};
`;

const QueueFilterButton = styled.button`
  position: relative;

  width: 32px;
  height: 32px;

  padding: 8px;

  cursor: pointer;
  border: none;
  border-radius: 4px;
  background-color: ${theme.buttonfill.brand.default};

  :hover {
    background-color: ${theme.buttonfill.brand.hover};
  }

  transition: background-color 0.1s ease-in-out;
`;

const IntentContainer = styled.div`
  width: 100%;

  padding: 0px 24px;

  overflow-y: auto;

  .infinite-scroll-component {
    overflow: hidden !important;
  }
`;

const UpcomingEventContainer = styled(FlexDiv)<{ expanded: boolean }>`
  gap: ${({ expanded }) => (expanded ? 0 : 8)}px;
  transition: gap 0.2s ease;
`;

// TODO: cleanup the modal wrapper styled components. Currently taken from dash V1
const slideInAnimation = keyframes`
  0% { margin-left: 0px; width: 0px; }
  100% { margin-left: 0px; width: 552px; }
`;
const DarkDiv2 = styled.div`
  position: fixed;
  top: 0px;
  bottom: 0px;
  left: -72px;
  right: 0px;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;
const SlideInDiv = styled.div`
  position: absolute;
  left: 72px;
  width: calc(478px + 72px);
  min-height: 100vh;
  overflow: hidden;
  animation-name: ${slideInAnimation};
  animation-duration: 700ms;
  z-index: 8;
  background-color: ${theme.WHITE_COLOR};
`;

const LoadingState = ({ selectedTabDialQueue }: { selectedTabDialQueue: LeadCardParentQueue }) => {
  return (
    <FlexDiv direction="column" padding="0px 24px" gap={8}>
      <SkeletonBlock
        width="100%"
        height={400}
        borderRadius={8}
        darkPulseStateColor="rgba(14, 41, 113, 0.50)"
        lightPulseStateColor="rgba(14, 40, 113, 0.3)"
        animationDuration={2}
      >
        <FlexDiv direction="column" align="center" justify="center" gap={16} height="100%">
          <Spinner size={48} />
          <AppText fontSize={12} fontWeight={500} lineHeight={18} color={theme.text.neutral.inverse}>
            {formatLoadingText(selectedTabDialQueue)}
          </AppText>
        </FlexDiv>
      </SkeletonBlock>
      <SkeletonBlock
        width="100%"
        height={50}
        borderRadius={8}
        darkPulseStateColor="rgba(14, 41, 113, 0.50)"
        lightPulseStateColor="rgba(14, 40, 113, 0.3)"
        animationDuration={2}
        delayNumber={1}
        opacity={1}
      />
      <SkeletonBlock
        width="100%"
        height={50}
        borderRadius={8}
        darkPulseStateColor="rgba(14, 41, 113, 0.50)"
        lightPulseStateColor="rgba(14, 40, 113, 0.3)"
        animationDuration={2}
        delayNumber={1.5}
        opacity={0.8}
      />
      <SkeletonBlock
        width="100%"
        height={50}
        borderRadius={8}
        darkPulseStateColor="rgba(14, 41, 113, 0.50)"
        lightPulseStateColor="rgba(14, 40, 113, 0.3)"
        animationDuration={2}
        delayNumber={2}
        opacity={0.6}
      />
      <SkeletonBlock
        width="100%"
        height={50}
        borderRadius={8}
        darkPulseStateColor="rgba(14, 41, 113, 0.50)"
        lightPulseStateColor="rgba(14, 40, 113, 0.3)"
        animationDuration={2}
        delayNumber={2.5}
        opacity={0.4}
      />
      <SkeletonBlock
        width="100%"
        height={50}
        borderRadius={8}
        darkPulseStateColor="rgba(14, 41, 113, 0.50)"
        lightPulseStateColor="rgba(14, 40, 113, 0.3)"
        animationDuration={2}
        delayNumber={3}
        opacity={0.2}
      />
    </FlexDiv>
  );
};

const ClearCustomQueueDiv = styled.div`
  background: transparent;
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  height: max-content;
  width: 100%;
  margin-top: 2px;
`;
